import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const slides = [
  {
    id: 3,
    tag: "USP Packaging",
    title: "RFID-Based Authentication System for Thermal Transfer Printers",
    description:
      "Gain clear visibility into your financial future. Predict your income and plan ahead effortlessly.",
    bottomText: "Stay ahead of your finances.",
    imgSrc:
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-30%20at%2011.29.09%20AM.jpeg",
    bgColor: "bg-[#d4d4d8]", // darker gray
    gallery: [],
  },
  {
    id: 2,
    tag: "Arabian Mandhi",
    title: "Custom Restaurant Management System Across 7 Branches.",
    description:
      "Understand the core drivers of your cashflow. Monitor key metrics and get actionable insights to grow.",
    bottomText: "Data-driven decisions made simple.",
    imgSrc:
      "https://ik.imagekit.io/cly20eovra/ChatGPT%20Image%20Jul%2030,%202026,%2012_03_04%20PM.png",
    bgColor: "bg-[#e4e4e7]", // slightly darker gray
    gallery: [
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.55.56%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.55.57%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.55.58%20PM%20(1).jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.55.58%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.55.59%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.56.00%20PM%20(1).jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.56.00%20PM%20(2).jpeg"
    ],
  },
  {
    id: 1,
    tag: "Kaliraj IAS Academy",
    title: "Secure LMS for Premium Course Delivery",
    description:
      "See recurring costs, upcoming payments, and contract obligations before they hit your balance.",
    bottomText: "No more \"we forgot this renewal\" moments.",
    imgSrc:
      "https://ik.imagekit.io/cly20eovra/Screenshot%202026-07-30%20112633.png",
    bgColor: "bg-[#f4f4f5]", // light gray
    gallery: [
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.07.06%20PM.jpeg?updatedAt=1785325419797",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.09.30%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.09.49%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.10.09%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.10.39%20PM.jpeg",
      "https://ik.imagekit.io/cly20eovra/WhatsApp%20Image%202026-07-29%20at%204.11.04%20PM.jpeg",
    ],
  },
];

const ScrollyTelling = () => {
  const containerRef = useRef(null);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openGallery = (gallery) => {
    if (gallery && gallery.length > 0) {
      setSelectedGallery(gallery);
      setIsModalOpen(true);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }
  };

  const closeGallery = () => {
    setIsModalOpen(false);
    setSelectedGallery(null);
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    let ctx = gsap.context(() => {
      const panels = gsap.utils.toArray(".scrolly-panel");

      // Set initial positions for all panels so they start below the screen
      gsap.set(panels, { yPercent: 100 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          pin: true,
          scrub: 1,
          end: () => "+=" + window.innerHeight * panels.length, // Scroll duration based on number of slides
        },
      });

      // Animate each panel sliding up one by one over the base text layer
      panels.forEach((panel, i) => {
        if (i === 0) {
          tl.to(panel, {
            yPercent: 0,
            ease: "none",
          });
        } else {
          const prevPanel = panels[i - 1];
          const prevInnerCard = prevPanel.querySelector(".inner-card");

          tl.to(panel, {
            yPercent: 0,
            ease: "none",
          })
          .to(prevInnerCard, {
            scale: 0.95,
            yPercent: -2,
            opacity: 0,
            ease: "none"
          }, "<"); // Animate at the same time as the panel slides in
        }
      });
    }, containerRef);

    return () => {
      ctx.revert();
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <>
      {/* Modal Popup */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-10">
          <button
            onClick={closeGallery}
            className="fixed top-6 left-6 md:top-8 md:left-8 text-white hover:text-black transition-all bg-white/20 hover:bg-white px-5 py-2.5 rounded-full z-[100000] flex items-center space-x-2 backdrop-blur-md border border-white/30 shadow-lg cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="font-semibold text-sm md:text-base tracking-wide">Back</span>
          </button>
          
          <div className="w-full max-w-5xl h-full overflow-y-auto rounded-2xl hide-scrollbar flex flex-col gap-6 items-center p-4 mt-16 md:mt-12">
            {selectedGallery?.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Gallery image ${idx + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl bg-black/50"
              />
            ))}
          </div>
        </div>
      )}

      <div className="gsap-stable-wrapper">
        <section ref={containerRef} className="relative w-full h-screen overflow-hidden bg-white">
        
        {/* Base Layer: Intro Header Section */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center z-0">
          <h1 className="text-3xl md:text-5xl font-medium text-gray-900 leading-snug md:leading-tight mb-20 max-w-4xl">
            Crack Developers is a digital innovation agency for founders and scaling teams that helps you build custom software and scale what's next.
          </h1>

          <div className="flex flex-col md:flex-row justify-center md:justify-between items-start w-full max-w-4xl mx-auto gap-8 md:gap-4">
            {/* Feature 1 */}
            <div className="flex flex-row items-center md:items-start md:text-left text-left space-x-4">
              <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800">Custom Software Developer </h3>
                <p className="text-sm text-gray-500">Custom web & app development</p>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="flex flex-row items-center md:items-start md:text-left text-left space-x-4">
              <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800">Embedded and IOT services</h3>
                <p className="text-sm text-gray-500">Scalable cloud infrastructure</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex flex-row items-center md:items-start md:text-left text-left space-x-4">
              <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800">Maintenance & Support</h3>
                <p className="text-sm text-gray-500">Insights & UX Optimization</p>
              </div>
            </div>
          </div>
        </div>

  
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className="scrolly-panel absolute top-0 left-0 w-full h-full flex items-center justify-center p-4 md:p-8"
            style={{ zIndex: index + 1 }}
          >
            <div className="inner-card w-full max-w-5xl h-[70vh] flex flex-col md:flex-row bg-white/70 backdrop-blur-2xl rounded-[32px] overflow-hidden shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-white/50 relative mt-16 md:mt-24">
              {/* Left Content */}
              <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                <div className="mb-auto">
                  <div className="inline-flex items-center space-x-2 bg-white/80 text-gray-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6 shadow-sm border border-gray-100/50">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>{slide.tag}</span>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-4">
                    {slide.title}
                  </h2>

                  <p className="text-base text-gray-600 leading-relaxed max-w-sm">
                    {slide.description}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full mt-8 border-t border-gray-200/50 pt-5 gap-4 sm:gap-0">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <svg
                      className="w-5 h-5 text-gray-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">{slide.bottomText}</span>
                  </div>
                  
                  {slide.gallery && slide.gallery.length > 0 && (
                    <button 
                      onClick={() => openGallery(slide.gallery)}
                      className="inline-flex items-center space-x-2 text-sm font-semibold text-gray-900 hover:text-gray-600 transition-colors group cursor-pointer flex-shrink-0 whitespace-nowrap"
                    >
                      <span>View More</span>
                      <svg className="w-4 h-4 transform group-hover:translate-x-1 transition duration-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Right Content (Image/Graphic) */}
              <div className="w-full md:w-1/2 h-full relative overflow-hidden hidden md:block rounded-r-[32px] bg-gray-50">
                <img
                  src={slide.imgSrc}
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        ))}
        </section>
      </div>
    </>
  );
};

export default ScrollyTelling;
